import React, { useState } from 'react';
import { 
  Code, 
  Copy, 
  Check, 
  Play, 
  Zap,
  Globe,
  Database,
  ExternalLink
} from 'lucide-react';

interface APIEndpoint {
  method: 'GET' | 'POST' | 'WebSocket';
  path: string;
  description: string;
  tier: 'free' | 'pro' | 'enterprise';
  example: string;
  response?: string;
}

const endpoints: APIEndpoint[] = [
  {
    method: 'GET',
    path: '/api/oracle/prices',
    description: 'Get all token prices with confidence scores',
    tier: 'free',
    example: `curl -X GET "https://oracle.doh.money/api/oracle/prices" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "prices": [
    {
      "symbol": "ALPH",
      "price": 1.2345,
      "confidence": 0.95,
      "timestamp": 1640995200,
      "source": "doh-money"
    }
  ],
  "count": 42,
  "timestamp": 1640995200
}`
  },
  {
    method: 'GET',
    path: '/api/oracle/price/{symbol}',
    description: 'Get specific token price data',
    tier: 'free',
    example: `curl -X GET "https://oracle.doh.money/api/oracle/price/ALPH" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    response: `{
  "symbol": "ALPH",
  "price": 1.2345,
  "confidence": 0.95,
  "timestamp": 1640995200,
  "source": "doh-money",
  "change_24h": 0.0523
}`
  },
  {
    method: 'WebSocket',
    path: 'wss://oracle.doh.money/stream',
    description: 'Real-time price updates via WebSocket',
    tier: 'pro',
    example: `const ws = new WebSocket('wss://oracle.doh.money/stream');
ws.onmessage = (event) => {
  const data = JSON.parse(event.data);
  console.log('Price update:', data);
};`,
    response: `{
  "type": "price_update",
  "symbol": "ALPH",
  "price": 1.2346,
  "confidence": 0.96,
  "timestamp": 1640995260
}`
  },
  {
    method: 'GET',
    path: '/api/oracle/historical',
    description: 'Historical price data with time ranges',
    tier: 'pro',
    example: `curl -X GET "https://oracle.doh.money/api/oracle/historical" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -G -d "symbol=ALPH" -d "from=1640908800" -d "to=1640995200"`,
    response: `{
  "symbol": "ALPH",
  "data": [
    {
      "timestamp": 1640908800,
      "price": 1.2200,
      "volume": 1000000
    }
  ]
}`
  },
  {
    method: 'POST',
    path: '/api/webhooks/price-alerts',
    description: 'Set up price alert webhooks',
    tier: 'pro',
    example: `curl -X POST "https://oracle.doh.money/api/webhooks/price-alerts" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "symbol": "ALPH",
    "condition": "above",
    "threshold": 1.50,
    "webhook_url": "https://your-app.com/webhook"
  }'`,
    response: `{
  "alert_id": "alert_123",
  "status": "active",
  "created_at": 1640995200
}`
  }
];

const tierColors = {
  free: 'text-gray-400 bg-gray-500/10',
  pro: 'text-blue-400 bg-blue-500/10',
  enterprise: 'text-orange-400 bg-orange-500/10'
};

const methodColors = {
  GET: 'text-emerald-400 bg-emerald-500/10',
  POST: 'text-blue-400 bg-blue-500/10',
  WebSocket: 'text-purple-400 bg-purple-500/10'
};

export const APIDocs: React.FC = () => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'endpoints' | 'examples' | 'sdks'>('endpoints');

  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div className="bg-gray-900/10 backdrop-blur-sm rounded-2xl border border-gray-800/20 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl">
            <Code className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">API Documentation</h3>
            <p className="text-gray-400 text-sm">Integrate Oracle data into your applications</p>
          </div>
        </div>
        <a 
          href="https://docs.oracle.doh.money" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-300 rounded-lg transition-all duration-200 text-sm"
        >
          Full Docs
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { id: 'endpoints', label: 'Endpoints', icon: Globe },
          { id: 'examples', label: 'Examples', icon: Play },
          { id: 'sdks', label: 'SDKs', icon: Database }
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium ${
              activeTab === id
                ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                : 'bg-gray-800/30 text-gray-400 hover:bg-gray-700/30 border border-gray-800/30'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'endpoints' && (
        <div className="space-y-4">
          {endpoints.map((endpoint, index) => (
            <div key={index} className="bg-gray-900/30 rounded-xl border border-gray-800/40 overflow-hidden">
              {/* Endpoint Header */}
              <div className="p-4 border-b border-gray-800/40">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${methodColors[endpoint.method]}`}>
                      {endpoint.method}
                    </span>
                    <code className="text-blue-400 font-mono text-sm">{endpoint.path}</code>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${tierColors[endpoint.tier]}`}>
                      {endpoint.tier}
                    </span>
                  </div>
                </div>
                <p className="text-gray-400 text-sm">{endpoint.description}</p>
              </div>

              {/* Example & Response */}
              <div className="grid md:grid-cols-2 gap-4 p-4">
                {/* Request Example */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-medium text-sm">Request</h4>
                    <button
                      onClick={() => copyToClipboard(endpoint.example, index)}
                      className="flex items-center gap-1 px-2 py-1 bg-gray-800/50 hover:bg-gray-700/50 rounded text-xs text-gray-400 transition-colors"
                    >
                      {copiedIndex === index ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copiedIndex === index ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="bg-black/40 p-3 rounded-lg text-xs text-gray-300 overflow-x-auto">
                    <code>{endpoint.example}</code>
                  </pre>
                </div>

                {/* Response Example */}
                {endpoint.response && (
                  <div>
                    <h4 className="text-white font-medium text-sm mb-2">Response</h4>
                    <pre className="bg-black/40 p-3 rounded-lg text-xs text-gray-300 overflow-x-auto">
                      <code>{endpoint.response}</code>
                    </pre>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'examples' && (
        <div className="space-y-6">
          {/* JavaScript Example */}
          <div className="bg-gray-900/30 rounded-xl border border-gray-800/40 p-4">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="w-4 h-4 bg-yellow-400 rounded"></span>
              JavaScript/TypeScript
            </h4>
            <pre className="bg-black/40 p-4 rounded-lg text-sm text-gray-300 overflow-x-auto">
              <code>{`import axios from 'axios';

const oracleAPI = axios.create({
  baseURL: 'https://oracle.doh.money',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY'
  }
});

// Get all prices
const prices = await oracleAPI.get('/api/oracle/prices');
console.log(prices.data);

// Get specific price
const alphPrice = await oracleAPI.get('/api/oracle/price/ALPH');
console.log(\`ALPH Price: $\{alphPrice.data.price}\`);`}</code>
            </pre>
          </div>

          {/* Python Example */}
          <div className="bg-gray-900/30 rounded-xl border border-gray-800/40 p-4">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="w-4 h-4 bg-blue-400 rounded"></span>
              Python
            </h4>
            <pre className="bg-black/40 p-4 rounded-lg text-sm text-gray-300 overflow-x-auto">
              <code>{`import requests

class OracleAPI:
    def __init__(self, api_key):
        self.base_url = "https://oracle.doh.money"
        self.headers = {"Authorization": f"Bearer {api_key}"}
    
    def get_prices(self):
        response = requests.get(f"{self.base_url}/api/oracle/prices", 
                              headers=self.headers)
        return response.json()
    
    def get_price(self, symbol):
        response = requests.get(f"{self.base_url}/api/oracle/price/{symbol}", 
                              headers=self.headers)
        return response.json()

oracle = OracleAPI("YOUR_API_KEY")
prices = oracle.get_prices()
print(f"Found {len(prices['prices'])} tokens")`}</code>
            </pre>
          </div>
        </div>
      )}

      {activeTab === 'sdks' && (
        <div className="space-y-4">
          {[
            { name: 'JavaScript SDK', desc: 'Official SDK for Node.js and browsers', status: 'Available' },
            { name: 'Python SDK', desc: 'Official Python package with async support', status: 'Available' },
            { name: 'Go SDK', desc: 'Lightweight Go client library', status: 'Coming Soon' },
            { name: 'Rust SDK', desc: 'High-performance Rust client', status: 'Coming Soon' }
          ].map((sdk, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-900/30 rounded-xl border border-gray-800/40">
              <div>
                <h4 className="text-white font-medium">{sdk.name}</h4>
                <p className="text-gray-400 text-sm">{sdk.desc}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                sdk.status === 'Available' 
                  ? 'bg-emerald-500/20 text-emerald-400' 
                  : 'bg-orange-500/20 text-orange-400'
              }`}>
                {sdk.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Quick Start */}
      <div className="mt-6 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-4 h-4 text-blue-400" />
          <h4 className="text-white font-medium">Quick Start</h4>
        </div>
        <p className="text-gray-400 text-sm mb-3">
          Get your API key and start integrating Oracle data in minutes.
        </p>
        <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors text-sm">
          Get API Key
        </button>
      </div>
    </div>
  );
};
